import { config } from './env';
import { auth, getGuestAuth } from './firebase';
import type { ApiErrorBody } from './types';

/**
 * The one place the browser talks to slate-api.
 *
 * Auth: a Firebase ID token in `Authorization: Bearer`. Request-link visitors
 * send an *anonymous* token plus `X-Slate-Request-Token`, which is what makes
 * the API resolve them to a `requestLink` actor.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Nothing to sign in as: the caller should bounce to /login. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** 402: free-tier video cap, or an org whose subscription lapsed. */
  get isQuota(): boolean {
    return this.status === 402;
  }
}

/** A network failure, a CORS rejection, an offline tab. Never a 4xx. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach Slate. Check your connection and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export type AuthMode =
  /** 401 locally, without a round trip, when nobody is signed in. */
  | 'required'
  /** Send a token if we have one; the endpoint works either way. */
  | 'optional'
  /** Never send a token. */
  | 'none';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: AuthMode;
  /** Present on /r/:token calls: use the guest session + this link token. */
  requestToken?: string;
  signal?: AbortSignal;
}

async function idTokenFor(options: RequestOptions): Promise<string | null> {
  const instance = options.requestToken ? getGuestAuth() : auth;
  const user = instance.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    // An expired refresh token (revoked session, password change) lands here.
    return null;
  }
}

function isJson(response: Response): boolean {
  return (response.headers.get('content-type') ?? '').includes('application/json');
}

async function readError(response: Response): Promise<ApiError> {
  if (isJson(response)) {
    try {
      const body = (await response.json()) as Partial<ApiErrorBody>;
      if (body.error?.message) {
        return new ApiError(response.status, body.error.code || 'error', body.error.message, body.error.details);
      }
    } catch {
      // Fall through to the generic message below.
    }
  }
  return new ApiError(
    response.status,
    'error',
    response.status >= 500
      ? 'Something went wrong on our end. Try again in a moment.'
      : `Request failed (${response.status}).`,
  );
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const mode: AuthMode = options.auth ?? 'required';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (mode !== 'none') {
    const token = await idTokenFor(options);
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    } else if (mode === 'required') {
      throw new ApiError(401, 'unauthorized', 'Sign in to continue.');
    }
  }

  if (options.requestToken) headers['X-Slate-Request-Token'] = options.requestToken;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${config.apiUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError(error);
  }

  if (!response.ok) throw await readError(response);

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  if (!isJson(response)) return undefined as T;
  return (await response.json()) as T;
}

/** Human-readable text for anything that can come out of a failed call. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) return error.message;
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return 'Permission was denied.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong.';
}
